import type {
  RecordingImportActionResponse,
  RecordingImportActionResult,
  RecordingImportActionSelection,
  RecordingImportCountsByCode,
  RecordingImportCountsByCodeResponse,
  RecordingImportPage,
  RecordingImportPageResponse,
  RecordingImportSortOrder,
  RecordingImportStatus,
  RecordingImportStatusCounts,
  RecordingImportStatusCountsResponse,
} from "@repo/app-types/entities/recordingImport";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The customer-facing recording-import surface, one set of endpoints for every
 * provider: `/integrations/:integrationId/recording-imports`. The list, the
 * status counts and the chip counts of one integration share one tag keyed on
 * the integration id, so a reprocess or skip refetches all three.
 */

function buildRecordingImportTag(integrationId: string) {
  return buildEntityTag(TagType.RecordingImport, integrationId);
}

function buildRecordingImportUrl(integrationId: string, suffix = "") {
  return `/integrations/${integrationId}/recording-imports${suffix}`;
}

export interface RecordingImportListArgs {
  integrationId: string;
  /** The one status shown; the server requires exactly one. */
  status: RecordingImportStatus;
  /** The `intaken_at` sort. */
  order: RecordingImportSortOrder;
  /** The selected chip: null = every code, `__none__` = no code, else the code. */
  errorCode: string | null;
}

export interface RecordingImportActionArgs {
  integrationId: string;
  selection: RecordingImportActionSelection;
}

export const recordingImportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Every arg is part of the cache key, so switching status tab, chip or sort
    // starts a fresh page 1.
    recordingImports: builder.infiniteQuery<
      RecordingImportPage,
      RecordingImportListArgs,
      string | null,
      RecordingImportPageResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: buildRecordingImportUrl(queryArg.integrationId),
        params: {
          status: queryArg.status,
          order: queryArg.order,
          ...(queryArg.errorCode ? { errorCode: queryArg.errorCode } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, { integrationId }) => [
        buildRecordingImportTag(integrationId),
      ],
    }),

    // The status tabs' badges.
    recordingImportStatusCounts: builder.query<
      RecordingImportStatusCounts,
      { integrationId: string }
    >({
      query: ({ integrationId }) =>
        buildRecordingImportUrl(integrationId, "/counts"),
      transformResponse: (response: RecordingImportStatusCountsResponse) =>
        response.data.counts,
      providesTags: (_result, _error, { integrationId }) => [
        buildRecordingImportTag(integrationId),
      ],
    }),

    // The filter chips for one status: counts by code, not narrowed by the
    // selected chip, so picking one doesn't refetch (and blank) them.
    recordingImportCountsByCode: builder.query<
      RecordingImportCountsByCode,
      { integrationId: string; status: RecordingImportStatus }
    >({
      query: ({ integrationId, status }) => ({
        url: buildRecordingImportUrl(integrationId, "/counts-by-code"),
        params: { status },
      }),
      transformResponse: (response: RecordingImportCountsByCodeResponse) =>
        response.data,
      providesTags: (_result, _error, { integrationId }) => [
        buildRecordingImportTag(integrationId),
      ],
    }),

    reprocessRecordingImports: builder.mutation<
      RecordingImportActionResult,
      RecordingImportActionArgs
    >({
      query: ({ integrationId, selection }) => ({
        method: "POST",
        url: buildRecordingImportUrl(integrationId, "/reprocess"),
        body: selection,
      }),
      transformResponse: (response: RecordingImportActionResponse) =>
        response.data,
      invalidatesTags: (_result, _error, { integrationId }) => [
        buildRecordingImportTag(integrationId),
      ],
    }),

    skipRecordingImports: builder.mutation<
      RecordingImportActionResult,
      RecordingImportActionArgs
    >({
      query: ({ integrationId, selection }) => ({
        method: "POST",
        url: buildRecordingImportUrl(integrationId, "/skip"),
        body: selection,
      }),
      transformResponse: (response: RecordingImportActionResponse) =>
        response.data,
      invalidatesTags: (_result, _error, { integrationId }) => [
        buildRecordingImportTag(integrationId),
      ],
    }),
  }),
});

export const {
  useRecordingImportsInfiniteQuery,
  useRecordingImportStatusCountsQuery,
  useRecordingImportCountsByCodeQuery,
  useReprocessRecordingImportsMutation,
  useSkipRecordingImportsMutation,
} = recordingImportApi;
